import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, BookOpen, Sparkles, BookMarked, UserRound } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

/* Persistent bottom tab bar, always rendered (the app always lives inside a
   fixed-width "phone frame" now — see AppLayout.jsx — there's no separate
   desktop layout left to gate this behind) — the fast path to the app's 5
   highest-traffic destinations, so a user isn't opening the hamburger
   drawer for every single navigation. Everything else (Community, Teachers,
   My Halaqa) is unchanged and still one tap away in that same drawer —
   this is purely additive, nothing was removed to make room for it.
   (Two deliberate exceptions removed from the sidebar/drawer since: Voice
   Notes — now reachable from the Teachers page (C8); and Search — now only
   the inline box inside the My Quran reader.)

   `absolute` (not `fixed`) so it anchors to the phone-frame div in
   AppLayout.jsx (which is `relative`) instead of the real browser viewport.

   Deliberately standalone (does NOT import from Sidebar.jsx) — that file
   has been touched multiple times already for delicate positioning fixes,
   so a few duplicated lines here is a safer trade than coupling to it. */
const TABS = [
  { to: "/app", icon: Home, labelKey: "tabbar.home", testid: "tabbar-home" },
  { to: "/app/quran", icon: BookOpen, labelKey: "tabbar.quran", testid: "tabbar-quran" },
  { to: "/app/tadabbur", icon: Sparkles, labelKey: "tabbar.tadabbur", testid: "tabbar-tadabbur" },
  { to: "/app/hifz", icon: BookMarked, labelKey: "tabbar.hifz", testid: "tabbar-hifz" },
  { to: "/app/profile", icon: UserRound, labelKey: "tabbar.profile", testid: "tabbar-profile" },
];

export default function MobileTabBar() {
  const { t } = useLanguage();
  const location = useLocation();
  const currentTab = new URLSearchParams(location.search).get("tab");

  return (
    // Height is the base bar height PLUS the safe-area inset (not just the
    // base height with the inset as padding carved out of it) — otherwise
    // the safe-area padding-bottom eats into the fixed height, and the
    // icons/labels (centered via items-stretch + justify-center) end up
    // visually pushed up against the top border on any device with a real
    // bottom inset (gesture-nav Android phones, iPhone home indicator).
    // AppLayout's content clearance already assumes this taller total.
    <nav
      aria-label={t("tabbar.primary")}
      data-testid="mobile-tab-bar"
      className="lg:hidden absolute inset-x-0 bottom-0 z-30 flex items-stretch justify-around border-t border-sand-300 bg-white/95 backdrop-blur-sm h-[calc(var(--mobile-tabbar-h)_+_env(safe-area-inset-bottom))] pb-[env(safe-area-inset-bottom)]"
    >
      {TABS.map((l) => {
        // Mirrors (does not import) NavGroups's /app/quran-vs-?tab=search
        // active-state special case in Sidebar.jsx (its "C5" comment) — a
        // user landing on /app/quran?tab=search via the drawer shouldn't
        // falsely light up "Quran" here. Keep in sync if that logic changes.
        const isQuranPath = l.to === "/app/quran";
        return (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/app"}
            data-testid={l.testid}
            className={({ isActive }) => {
              const active = isQuranPath
                ? location.pathname === "/app/quran" && currentTab !== "search"
                : isActive;
              return `flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors ${
                active ? "text-forest-600" : "text-muted-foreground"
              }`;
            }}
          >
            <l.icon className="h-5 w-5" />
            <span>{t(l.labelKey)}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}
